import React, { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Avatar,
  Box,
  CardActionArea,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import {
  CompanyNavigationItem,
  CompanyNavigationTone,
  getCompanyNavigationGroups,
} from '../navigation/companyNavigation';
import { useDomainSettingsQuery } from '../settings/useDomainSettingsQuery';
import { AttentionItemsStrip } from '../components/notifications/AttentionItemsStrip';

interface QuickAccessItem extends CompanyNavigationItem {
  groupTitle: string;
  tone: CompanyNavigationTone;
}

export const CompanyDashboardPage: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { user } = useAuth();
  const companyId = user?.company?.id;
  const { data: domainSettings } = useDomainSettingsQuery(companyId);
  const navigationFeatures = useMemo(
    () => ({
      multipleLocationsEnabled: Boolean(domainSettings?.multipleLocations),
      emailConnectivityEnabled: domainSettings?.emailConnectivity !== false,
    }),
    [domainSettings?.multipleLocations, domainSettings?.emailConnectivity]
  );

  const navigationGroups = useMemo(
    () => getCompanyNavigationGroups(t, user?.role, user?.permissions, 'dashboard', navigationFeatures),
    [navigationFeatures, t, user?.permissions, user?.role]
  );

  const quickAccessItems = useMemo<QuickAccessItem[]>(
    () =>
      navigationGroups.flatMap((group) =>
        group.items.map((item) => ({
          ...item,
          groupTitle: group.title,
          tone: group.tone,
        }))
      ),
    [navigationGroups]
  );

  return (
    <Box
      sx={{
        boxSizing: 'border-box',
        px: { xs: 1, sm: 2 },
        py: { xs: 1, md: 1.5 },
        height: { xs: 'auto', md: 'calc(100vh - 96px)' },
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
        overflow: { xs: 'visible', md: 'hidden' },
      }}
    >
      <Box
        sx={{
          p: { xs: 0.25, md: 0.5 },
          flexGrow: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <AttentionItemsStrip />
        <Box
          sx={{
            flexGrow: 1,
            minHeight: 0,
            display: 'grid',
            alignContent: 'start',
            gap: { xs: 1.25, md: 1.5 },
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))',
              sm: 'repeat(3, minmax(0, 1fr))',
              md: 'repeat(4, minmax(0, 1fr))',
              lg: 'repeat(5, minmax(0, 1fr))',
            },
          }}
        >
          {quickAccessItems.map((item) => {
            const ItemIcon = item.icon;

            return (
              <Paper
                key={item.id}
                variant="outlined"
                sx={{
                  borderRadius: 1.75,
                  overflow: 'hidden',
                  minHeight: { xs: 104, md: 96 },
                  borderColor: alpha(theme.palette[item.tone].main, 0.14),
                  backgroundColor: alpha(theme.palette[item.tone].main, 0.04),
                  transition: theme.transitions.create(
                    ['border-color', 'box-shadow', 'transform', 'background-color'],
                    {
                      easing: theme.transitions.easing.easeInOut,
                      duration: theme.transitions.duration.shorter,
                    }
                  ),
                  '&:hover': {
                    borderColor: alpha(theme.palette[item.tone].main, 0.28),
                    backgroundColor: alpha(theme.palette[item.tone].main, 0.08),
                    boxShadow: theme.shadows[3],
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                <CardActionArea
                  component={RouterLink}
                  to={item.path}
                  aria-label={`${item.title} - ${item.groupTitle}`}
                  sx={{
                    height: '100%',
                    px: { xs: 1.25, md: 1.5 },
                    py: { xs: 1.25, md: 1.35 },
                    display: 'flex',
                    alignItems: 'stretch',
                    borderRadius: 'inherit',
                    textDecoration: 'none',
                    '&.Mui-focusVisible': {
                      outline: `2px solid ${alpha(theme.palette[item.tone].main, 0.32)}`,
                      outlineOffset: -2,
                    },
                  }}
                >
                  <Stack spacing={1.25} sx={{ width: '100%' }}>
                    <Stack
                      direction="row"
                      alignItems="flex-start"
                      justifyContent="space-between"
                      spacing={1}
                    >
                      <Avatar
                        variant="rounded"
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: alpha(theme.palette[item.tone].main, 0.12),
                          color: `${item.tone}.main`,
                          flexShrink: 0,
                        }}
                      >
                        <ItemIcon fontSize="small" />
                      </Avatar>
                      <Typography
                        variant="caption"
                        sx={{
                          color: `${item.tone}.main`,
                          fontWeight: 700,
                          lineHeight: 1.2,
                          textAlign: 'right',
                          textTransform: 'uppercase',
                          letterSpacing: 0.35,
                          maxWidth: '58%',
                        }}
                      >
                        {item.groupTitle}
                      </Typography>
                    </Stack>

                    <Typography
                      variant="body2"
                      fontWeight={700}
                      sx={{
                        lineHeight: 1.25,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textAlign: 'left',
                      }}
                    >
                      {item.title}
                    </Typography>
                  </Stack>
                </CardActionArea>
              </Paper>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};
